import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import EmailPreview from "./EmailPreview";
import "./LaunchedTemplates.scss";
import { toast } from "react-toastify";

/**
 * Review & save form for a launched template (from /launched-notifications
 * or /launched-automations), turned into a live scheduled event / cron job.
 *
 * The marketplace has a direct Download action; this form is the
 * edit-before-download path and always saves the edited template directly.
 *
 * status is no longer editable in the UI - always sent as "Enabled".
 * templateStatus is no longer editable in the UI either - always sent as "".
 *
 * fromEmailSource and toEmailSourceFrom are two DIFFERENT backend enums
 * (confirmed from separate 500 errors) - do not merge these lists.
 *
 * Notify targets (notifyToSource / notifyToEmails) are derived entirely
 * from "To email source" + the target picker below it - there's no more
 * separate manual "add a row" UI. DEPARTMENT / BRANCH / ROLE / COMPANY
 * picks are sent as notifyToSource; SPECIFIC_EMPLOYEES picks resolve each
 * employee's email and are sent as notifyToEmails instead.
 */

// Confirmed from a 500 error.
const TRIGGER_DATE_OPTIONS = ["OnDATE", "DateAFTER", "DateBEFORE"];

const STATUS_SENT_TO_BACKEND = "ENABLED";
const EMAIL_FIELDS = ["subject", "body"];
const IN_APP_FIELDS = ["notificationHeading", "notificationMessage"];

// Quill leaves markup like "<p><br></p>" behind when the editor is cleared, so
// a plain `!value.trim()` isn't enough to detect an empty body.
const isRichTextEmpty = (value) =>
  !String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const isFieldEmpty = (field, value) =>
  field === "body" ? isRichTextEmpty(value) : !String(value || "").trim();

// TriggerDate uses mixed casing ("OnDATE", "DateAFTER", "DateBEFORE").
const matchEnumCaseInsensitive = (raw, options) => {
  if (!raw) return "";
  const found = options.find(
    (o) => o.toLowerCase() === raw.toString().trim().toLowerCase(),
  );
  return found || "";
};

// cronJobExpression is 6 space-separated fields: seconds minute hour day month weekday
// e.g. "* 0 8 * * *" = every day at 08:00. We only expose hour/minute for editing;
// the rest stay as "*" to match the pattern seen in existing data.
const parseCronTime = (expr) => {
  if (!expr) return { hour: "8", minute: "0" };
  const parts = expr.split(" ");
  return { minute: parts[1] ?? "0", hour: parts[2] ?? "8" };
};

const buildCronExpression = (hour, minute) => `* ${minute} ${hour} * * *`;

const normalizeToEmailSourceFrom = (value) => {
  if (!value) return "";

  const normalized = String(value).trim().toLowerCase();

  const map = {
    "respective manager": "RESPECTIVE_MANAGER",
    "respective interviewer": "RESPECTIVE_INTERVIEWER",
    branch: "BRANCH",
    "all members in project": "ALL_MEMBERS_IN_PROJECT",
    "respective accountant": "RESPECTIVE_ACCOUNTANT",
    "respective consultant": "RESPECTIVE_CONSULTANT",
    "respective lead assigned employee": "RESPECTIVE_LEAD_ASSIGNED_EMPLOYEE",
    "respective recruiter hr": "RESPECTIVE_RECRUITER_HR",
    "respective project member": "RESPECTIVE_PROJECT_MEMBER",
    "respective hod": "RESPECTIVE_HOD",
    "respective interviewers": "RESPECTIVE_INTERVIEWERS",
    "respective operation stage members": "RESPECTIVE_OPERATION_STAGE_MEMBERS",
    "respective stage task assigned members":
      "RESPECTIVE_STAGE_TASK_ASSIGNED_MEMBERS",
    "respective task assigned members": "RESPECTIVE_TASK_ASSIGNED_MEMBERS",
    role: "ROLE",
    "respective hr": "RESPECTIVE_HR",
    "specific employees": "SPECIFIC_EMPLOYEES",
    "respective employee": "RESPECTIVE_EMPLOYEE",
    company: "COMPANY",
    candidate: "CANDIDATE",
    allemp: "ALLEMP",
    "all emp": "ALLEMP",
    "all employees": "ALLEMP",
    department: "DEPARTMENT",
    "respective client assigned employee":
      "RESPECTIVE_CLIENT_ASSIGNED_EMPLOYEE",
    "respective vacancy creator": "RESPECTIVE_VACANCY_CREATOR",
    authority: "AUTHORITY",
    "all respective next round interviewers":
      "ALL_RESPECTIVE_NEXT_ROUND_INTERVIEWERS",
  };

  return map[normalized] || value;
};

const normalizeFromEmailSource = (value) => {
  if (!value) return "";

  const normalized = String(value).trim().toLowerCase();

  const map = {
    "respective manager": "RESPECTIVE_MANAGER",
    "respective interviewer": "RESPECTIVE_INTERVIEWER",
    candidate: "CANDIDATE",
    "respective accountant": "RESPECTIVE_ACCOUNTANT",
    "respective recruiter hr": "RESPECTIVE_RECRUITER_HR",
    "respective sales manager": "RESPECTIVE_SALES_MANAGER",
    "respective hod": "RESPECTIVE_HOD",
    specific: "SPECIFIC",
    "respective production manager": "RESPECTIVE_PRODUCTION_MANAGER",
    "respective hr": "RESPECTIVE_HR",
    tool: "TOOL",
    "respective employee": "RESPECTIVE_EMPLOYEE",
    company: "COMPANY",
  };

  return map[normalized] || value;
};

// Keep backend enum values for saving, while presenting plain-language names
// throughout the editor.
const readableLabel = (value) =>
  String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const triggerDateLabel = (value) =>
  ({
    OnDATE: "On date",
    DateAFTER: "After date",
    DateBEFORE: "Before date",
  })[value] || readableLabel(value);

const sourceOption = (source, type) => {
  const code =
    type === "from" ? source?.fromEmailSourceCode : source?.toEmailSourceCode;
  const name =
    type === "from" ? source?.fromEmailSourceName : source?.toEmailSourceName;
  const sourceValue = code || name || "";
  const normalizeSource =
    type === "from" ? normalizeFromEmailSource : normalizeToEmailSourceFrom;

  return {
    // Mapping APIs can return display labels such as "Respective Employee".
    // Keep the select value as the backend enum regardless of which form the
    // GET response uses, so it also matches the value loaded from the record.
    value: normalizeSource(sourceValue),
    label: readableLabel(name || code),
  };
};

// GET /admin/notification-types/all returns { notificationTypeId, type, name }.
// Normalize to the { notificationTypeId, notificationType, label } shape used
// throughout this form (notificationType drives EMAIL/IN_APP field toggling
// and payload ids; label is what's shown on the checkbox pill).
const mapAdminNotificationType = (adminType) => ({
  notificationTypeId: adminType.notificationTypeId,
  notificationType: (adminType.type || adminType.name || "").toUpperCase(),
  label: readableLabel(adminType.name || adminType.type),
});

const LaunchedTemplateReviewForm = ({
  item,
  type,
  onClose,
  onSaved,
  // Explore pages pass true: they save through the /save endpoint.
  // Everywhere else (Main pages) uses the regular create (POST) / update
  // (PUT) endpoints. templateCode (from the GET response) is sent on all
  // of them, or null when the record has none.
  useSaveEndpoint = false,
}) => {
  const { user } = useAuth();
  const isEvent = type === "event";
  const template = isEvent ? item.eventTemplate : item.cronJobTemplate;
  // The "as-sent" rendered version - placeholders already filled in with
  // this specific record's real values - used purely to show what the
  // email/notification actually looked like. `cronJobTemplate` /
  // `eventTemplate` (above) stay the source of truth for editing, since
  // those still contain the raw {Placeholder} tokens.
  const templatePreview = isEvent
    ? item.eventTemplatePreview
    : item.cronJobTemplatePreview;
  // Cron-job launched items don't carry a notificationTypes array from the
  // backend (unlike event items) - fetch the full admin list as a fallback
  // so the checkboxes and template-field toggling below still work, using
  // real notificationTypeId values instead of a hardcoded guess.
  const [fetchedNotificationTypes, setFetchedNotificationTypes] = useState([]);
  const itemHasNotificationTypes = !!item.notificationTypes?.length;
  // The checkbox list must always reflect every notification type that
  // exists, not just the ones this particular record currently has
  // selected - otherwise deselecting a type, saving, and reopening the
  // form makes that type vanish from the options entirely (it's no longer
  // in item.notificationTypes, so there'd be nothing left to re-select).
  // The admin list is the source of truth for *which options render*;
  // item.notificationTypes is only used below to seed the initial
  // selection.
  const notificationTypeOptions = useMemo(
    () =>
      fetchedNotificationTypes.length
        ? fetchedNotificationTypes
        : itemHasNotificationTypes
          ? item.notificationTypes
          : [],
    [
      fetchedNotificationTypes,
      itemHasNotificationTypes,
      item.notificationTypes,
    ],
  );
  const mappingId = isEvent
    ? (item.eventTypeId ?? item.eventId)
    : (item.cronJobId ?? item.scheduledCronJobId ?? item.cronJobTypeId);

  // IDs come from the signed-in session. The fallbacks keep existing records
  // saveable if an older access response has not exposed these fields yet.
  const authenticatedEmployeeId =
    user?.employeeId ??
    user?.employeeID ??
    user?.empId ??
    user?.employee?.employeeId ??
    item.employeeId;
  const authenticatedCompanyId =
    user?.companyId ??
    user?.companyID ??
    user?.tenantId ??
    user?.company?.companyId ??
    process.env.REACT_APP_COMPANY_ID ??
    item.companyId;

  const [taskName, setTaskName] = useState(item.taskName || "");

  const [toEmailSourceFrom, setToEmailSourceFrom] = useState(
    normalizeToEmailSourceFrom(item.toEmailSourceFrom),
  );
  const [fromEmailSource, setFromEmailSource] = useState(
    normalizeFromEmailSource(item.fromEmailSource),
  );

  // Cron-job-only fields
  const initialCronTime = parseCronTime(item.cronJobExpression);
  const [cronHour, setCronHour] = useState(initialCronTime.hour);
  const [cronMinute, setCronMinute] = useState(initialCronTime.minute);
  const [triggerDate, setTriggerDate] = useState(
    matchEnumCaseInsensitive(item.triggerDate, TRIGGER_DATE_OPTIONS),
  );
  const triggerDateMatched = !!matchEnumCaseInsensitive(
    item.triggerDate,
    TRIGGER_DATE_OPTIONS,
  );
  const [days, setDays] = useState(item.days ?? "");

  const [selectedNotificationTypeIds, setSelectedNotificationTypeIds] =
    useState(
      itemHasNotificationTypes
        ? item.notificationTypes.map((n) => n.notificationTypeId)
        : [],
    );

  // Always fetch the full admin notification-types list so the checkboxes
  // always show every available type, not just whichever ones this record
  // currently has selected (see notificationTypeOptions above). Only fall
  // back to defaulting the selection to "all" when the item itself didn't
  // already tell us which types were selected (e.g. cron jobs) - event
  // items keep whatever selection came from item.notificationTypes.
  useEffect(() => {
    let cancelled = false;
    const fetchNotificationTypes = async () => {
      try {
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/notification-types/all`,
        );
        const mapped = (data?.data || []).map(mapAdminNotificationType);
        if (cancelled) return;
        setFetchedNotificationTypes(mapped);
        if (!itemHasNotificationTypes) {
          setSelectedNotificationTypeIds(
            mapped.map((n) => n.notificationTypeId),
          );
        }
      } catch (notificationTypeError) {
        console.error(
          "Failed to fetch notification types",
          notificationTypeError,
        );
      }
    };
    fetchNotificationTypes();
    return () => {
      cancelled = true;
    };
  }, [itemHasNotificationTypes]);

  // Which template fields render depends on which notification types are
  // selected: EMAIL -> subject + body (with placeholders), IN_APP -> the
  // notification heading/message fields. Selecting both shows all four.
  // Hiding a field never drops its data - templateFields keeps whatever was
  // typed there and it's still sent in full on save.
  const selectedTypeNames = useMemo(
    () =>
      notificationTypeOptions
        .filter((n) =>
          selectedNotificationTypeIds.includes(n.notificationTypeId),
        )
        .map((n) => (n.notificationType || "").toUpperCase()),
    [notificationTypeOptions, selectedNotificationTypeIds],
  );
  const showEmailFields = selectedTypeNames.includes("EMAIL");
  const showInAppFields = selectedTypeNames.includes("IN_APP");

  const [templateFields, setTemplateFields] = useState({
    templateId: template?.templateId || 0,
    templateName: template?.templateName || "",
    subject: template?.subject || "",
    body: template?.body || "",
    htmlBody: template?.htmlBody ?? false,
    notificationHeading: template?.notificationHeading || "",
    notificationMessage: template?.notificationMessage || "",
  });

  const [companyEmailConfigs, setCompanyEmailConfigs] = useState([]);
  const [specificEmailConfigs, setSpecificEmailConfigs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Loaded lazily, only once "Authority" is chosen as the recipient type.
  const [authorities, setAuthorities] = useState([]);
  const [isLoadingAuthorities, setIsLoadingAuthorities] = useState(false);
  const [selectedFromEmailConfig, setSelectedFromEmailConfig] = useState("");
  // Raw already-mapped rows from the record being edited, kept around so we
  // can (a) preselect the target picker below and (b) reuse each row's real
  // `id` on save instead of always sending 0 (see notifyToEmailsPayload /
  // notifyToSourcePayload in doSave - sending id:0 for an already-mapped row
  // makes the backend insert a duplicate instead of updating it).
  const existingNotifyToEmails = item.notifyToEmails || [];
  const existingNotifyToSource = item.notifyToSource || [];
  // Preselect the target picker from whichever of the two arrays applies to
  // this record's toEmailSourceFrom. SPECIFIC_EMPLOYEES resolves against
  // notifyToEmails (by employeeId); every other source type resolves
  // against notifyToSource (by sourceId). Computed once from the initial
  // toEmailSourceFrom so it matches what's actually loaded for this item.
  const [selectedTargetIds, setSelectedTargetIds] = useState(() =>
    toEmailSourceFrom === "SPECIFIC_EMPLOYEES"
      ? existingNotifyToEmails.map((row) => row.employeeId).filter(Boolean)
      : existingNotifyToSource.map((row) => row.sourceId).filter(Boolean),
  );
  const [activeTemplateField, setActiveTemplateField] = useState("body");
  const templateFieldRefs = useRef({});

  // Keeps activeTemplateField pointed at a field that's actually on screen.
  // Without this, if only IN_APP is selected, clicking a placeholder chip
  // (which nobody has focused a field for yet) would fall back to "body" -
  // a field that isn't rendered - and silently write into it.
  useEffect(() => {
    setActiveTemplateField((current) => {
      const visibleFields = [
        ...(showEmailFields ? EMAIL_FIELDS : []),
        ...(showInAppFields ? IN_APP_FIELDS : []),
      ];
      if (visibleFields.includes(current)) return current;
      return visibleFields[0] ?? current;
    });
  }, [showEmailFields, showInAppFields]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Template fields that failed the required check on the last save attempt.
  // Drives the red border + inline message; cleared per-field as the user types.
  const [fieldErrors, setFieldErrors] = useState({});
  // "live" = the template currently being edited (raw placeholders).
  // "look" = templatePreview - how it actually rendered/sent, real values
  // filled in. Only offered when a preview payload came back for this item.
  const [previewMode, setPreviewMode] = useState("live");
  const [mappings, setMappings] = useState(null);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);

  useEffect(() => {
    if (!mappingId) return undefined;
    const fetchMappings = async () => {
      setIsLoadingMappings(true);
      try {
        const endpoint = isEvent
          ? `/scheduled-events/event-type/${mappingId}/mappings`
          : `/scheduled-cron-jobs/cron-job/${mappingId}/mappings`;
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        );
        setMappings(data?.data ?? null);
      } catch (mappingError) {
        console.error("Failed to fetch email source mappings", mappingError);
        setMappings(null);
      } finally {
        setIsLoadingMappings(false);
      }
    };
    fetchMappings();
    return undefined;
  }, [isEvent, mappingId]);

  const fromEmailOptions = useMemo(() => {
    const mapped = (mappings?.fromEmailSources || [])
      .map((source) => sourceOption(source, "from"))
      .filter((source) => source.value);
    return mapped;
  }, [mappings]);

  const toEmailOptions = useMemo(() => {
    const mapped = (mappings?.toEmailSources || [])
      .map((source) => sourceOption(source, "to"))
      .filter((source) => source.value);
    return mapped;
  }, [mappings]);

  useEffect(() => {
    const employeeApi = process.env.REACT_APP_API_EMPLOYEE_URL;
    if (!employeeApi) return undefined;

    const fetchRecipientOptions = async () => {
      try {
        const [
          companyResponse,
          departmentResponse,
          branchResponse,
          roleResponse,
          employeeResponse,
        ] = await Promise.all([
          axiosInstance.get(`${employeeApi}/company/email-config/All`),
          axiosInstance.get(`${employeeApi}/departments/all`),
          axiosInstance.get(`${employeeApi}/branches`),
          axiosInstance.get(`${employeeApi}/roles/enabled`),
          axiosInstance.get(`${employeeApi}/employees/All`),
        ]);
        setCompanyEmailConfigs(companyResponse.data?.data || []);
        setDepartments(departmentResponse.data?.data || []);
        setBranches(branchResponse.data?.data || []);
        setRoles(roleResponse.data?.data || []);
        setEmployees(employeeResponse.data?.data || []);
      } catch (recipientError) {
        console.error(
          "Failed to fetch recipient selector options",
          recipientError,
        );
      }
    };

    fetchRecipientOptions();
    return undefined;
  }, []);

  // GET /admin/authorities/all -> { data: [{ id, code, name, description }] }.
  // Only fetched when the recipient type is AUTHORITY (and only once), same
  // lazy pattern as specificEmailConfigs above. Also runs when an existing
  // record already has toEmailSourceFrom = AUTHORITY, so its saved picks
  // preselect in the dropdown.
  useEffect(() => {
    if (toEmailSourceFrom !== "AUTHORITY" || authorities.length) {
      return undefined;
    }

    let cancelled = false;
    const fetchAuthorities = async () => {
      setIsLoadingAuthorities(true);
      try {
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/authorities/all`,
        );
        if (!cancelled) setAuthorities(data?.data || []);
      } catch (authorityError) {
        console.error("Failed to fetch authorities", authorityError);
        if (!cancelled) toast.error("Couldn't load authorities.");
      } finally {
        setIsLoadingAuthorities(false);
      }
    };

    fetchAuthorities();
    return () => {
      cancelled = true;
    };
  }, [toEmailSourceFrom, authorities.length]);

  useEffect(() => {
    if (fromEmailSource !== "SPECIFIC" || specificEmailConfigs.length) {
      return undefined;
    }

    const fetchSpecificEmailConfigs = async () => {
      try {
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/email-config`,
        );
        const configs = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : data?.data
              ? [data.data]
              : [];

        setSpecificEmailConfigs(configs);
      } catch (specificEmailError) {
        console.error(
          "Failed to fetch specific email configurations",
          specificEmailError,
        );
      }
    };

    fetchSpecificEmailConfigs();
    return undefined;
  }, [fromEmailSource, specificEmailConfigs.length]);

  // Edit mode: the GET payload for this scheduled item already carries a
  // resolved fromEmail string (e.g. "mridulpramod8@gmail.com") but not an
  // emailConfigid, so the dropdown has nothing to bind to until we match it
  // ourselves. Once the relevant config list has loaded, find the entry
  // whose fromEmail matches and preselect it. Skipped once the user (or a
  // prior run of this effect) has already made a selection, so it never
  // clobbers manual changes.
  useEffect(() => {
    if (selectedFromEmailConfig) return;
    if (!item.fromEmail) return;

    const activeConfigs =
      fromEmailSource === "COMPANY"
        ? companyEmailConfigs
        : fromEmailSource === "SPECIFIC"
          ? specificEmailConfigs
          : [];
    if (!activeConfigs.length) return;

    const match = activeConfigs.find(
      (config) => config.fromEmail === item.fromEmail,
    );
    if (match) {
      setSelectedFromEmailConfig(String(match.emailConfigid));
    }
  }, [
    companyEmailConfigs,
    specificEmailConfigs,
    fromEmailSource,
    item.fromEmail,
    selectedFromEmailConfig,
  ]);

  const targetOptions = useMemo(() => {
    switch (toEmailSourceFrom) {
      case "DEPARTMENT":
        return departments.map((item) => ({
          value: item.departmentId,
          label: item.departmentName,
        }));
      case "BRANCH":
        return branches.map((item) => ({
          value: item.branchId,
          label: item.branchName,
        }));
      case "ROLE":
        return roles.map((item) => ({
          value: item.roleId,
          label: item.roleName,
        }));
      case "SPECIFIC_EMPLOYEES":
        return employees.map((item) => ({
          value: item.empId ?? item.employeeId,
          label:
            `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() ||
            item.employeeName ||
            item.name ||
            `Employee ${item.empId ?? item.employeeId}`,
        }));
      case "COMPANY":
        return companyEmailConfigs.map((item) => ({
          value: item.emailConfigid,
          label: `${item.fromEmail}${item.fullName ? ` (${item.fullName})` : ""}`,
        }));
      case "AUTHORITY":
        // value = authority id -> sent as notifyToSource[].sourceId on save.
        return authorities.map((item) => ({
          value: item.id,
          label: item.name,
        }));
      default:
        return [];
    }
  }, [
    toEmailSourceFrom,
    departments,
    branches,
    roles,
    employees,
    companyEmailConfigs,
    authorities,
  ]);

  // The picker normally hides itself when there's nothing to pick from.
  // For AUTHORITY the list arrives asynchronously, so keep the dropdown
  // visible (with a loading state) instead of having it pop in later.
  const showTargetPicker =
    targetOptions.length > 0 || toEmailSourceFrom === "AUTHORITY";

  const insertPlaceholder = (placeholder) => {
    const field = activeTemplateField || "body";
    const element = templateFieldRefs.current[field];
    const currentValue = templateFields[field] || "";

    if (field === "body" && element?.getEditor) {
      const editor = element.getEditor();
      const range = editor.getSelection(true);
      const index = range?.index ?? editor.getLength() - 1;
      const length = range?.length ?? 0;
      editor.deleteText(index, length, "user");
      editor.insertText(index, placeholder, "user");
      editor.setSelection(index + placeholder.length, 0, "silent");
      return;
    }
    const start = element?.selectionStart ?? currentValue.length;
    const end = element?.selectionEnd ?? currentValue.length;
    const nextValue = `${currentValue.slice(0, start)}${placeholder}${currentValue.slice(end)}`;

    updateTemplateField(field, nextValue);
    requestAnimationFrame(() => {
      const input = templateFieldRefs.current[field];
      if (input) {
        const cursor = start + placeholder.length;
        input.focus();
        input.setSelectionRange(cursor, cursor);
      }
    });
  };

  const toggleNotificationType = (id) => {
    // Errors for a type's fields are re-computed on the next save attempt.
    setFieldErrors({});
    setSelectedNotificationTypeIds((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
    );
  };

  // Detects whether the Body content contains actual HTML markup (as opposed
  // to plain text that merely contains a "<" character). Used to drive
  // templateFields.htmlBody automatically instead of a manual checkbox.
  const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\s*\/?>/i;
  const looksLikeHtml = (text) => HTML_TAG_REGEX.test(text || "");

  const updateTemplateField = (field, value) => {
    setFieldErrors((prev) => {
      if (!prev[field] || isFieldEmpty(field, value)) return prev;
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
    setTemplateFields((prev) => {
      const next = { ...prev, [field]: value };
      // Re-derive htmlBody in real time whenever the Body field changes, so
      // the backend request and the Live preview always match what's typed.
      if (field === "body") {
        next.htmlBody = looksLikeHtml(value);
      }
      return next;
    });
  };

  const idField = isEvent ? "scheduledEventId" : "scheduledCronJobId";

  const doSave = async (idOverride) => {
    // Non-Explore: an existing id means update (PUT), otherwise create (POST).
    const isUpdate = Boolean(idOverride);
    // templateCode comes from the GET response (master DB) and is passed back
    // unchanged on save, create and update. If the record has none -> null.
    const templateCodePayload =
      item.templateCode ||
      item.automation?.templateCode ||
      item.notification?.templateCode ||
      null;
    setSaving(true);
    setError(null);

    try {
      // Sender email address: resolved from the picked Company/Specific
      // email config (matched by emailConfigid) so the actual address goes
      // to the backend instead of an empty string. Stays "" for any other
      // fromEmailSource, where no config picker is shown.
      const activeEmailConfigs =
        fromEmailSource === "COMPANY"
          ? companyEmailConfigs
          : fromEmailSource === "SPECIFIC"
            ? specificEmailConfigs
            : [];
      const resolvedFromEmail =
        activeEmailConfigs.find(
          (config) =>
            String(config.emailConfigid) === String(selectedFromEmailConfig),
        )?.fromEmail || "";

      const notifyToEmailsPayload =
        toEmailSourceFrom === "SPECIFIC_EMPLOYEES"
          ? selectedTargetIds
              .map((empId) =>
                employees.find(
                  (e) => String(e.empId ?? e.employeeId) === String(empId),
                ),
              )
              .filter(Boolean)
              .map((employee) => {
                const employeeIdNum =
                  Number(employee.empId ?? employee.employeeId) || 0;
                // Reuse the id of an already-mapped row for this employee
                // (from the record being edited) so the backend updates it
                // in place. Only a target the user newly added gets id: 0,
                // which is correctly treated as a new row.
                const existingRow = existingNotifyToEmails.find(
                  (row) => Number(row.employeeId) === employeeIdNum,
                );
                return {
                  id: existingRow?.id || 0,
                  email: employee.companyEmail || employee.email || "",
                  employeeId: employeeIdNum,
                };
              })
              .filter((row) => row.email)
          : [];

      const notifyToSourcePayload =
        toEmailSourceFrom === "SPECIFIC_EMPLOYEES"
          ? []
          : selectedTargetIds.map((sourceId) => {
              const sourceIdNum = Number(sourceId) || 0;
              // Same reuse-existing-id logic as notifyToEmailsPayload above.
              const existingRow = existingNotifyToSource.find(
                (row) => Number(row.sourceId) === sourceIdNum,
              );
              return {
                id: existingRow?.id || 0,
                sourceId: sourceIdNum,
                employeeId: 0,
              };
            });

      if (isEvent) {
        const payload = {
          scheduledEventId: idOverride,
          taskName,
          eventTypeId: item.eventTypeId,
          notificationTypeIds: selectedNotificationTypeIds,
          toEmailSourceFrom: normalizeToEmailSourceFrom(toEmailSourceFrom),
          fromEmailSource: normalizeFromEmailSource(fromEmailSource),
          fromEmail: resolvedFromEmail,
          status: STATUS_SENT_TO_BACKEND,
          employeeId: Number(authenticatedEmployeeId) || 0,
          companyId: Number(authenticatedCompanyId) || 0,
          eventTemplate: templateFields,
          notifyToSource: notifyToSourcePayload,
          notifyToEmails: notifyToEmailsPayload,
          templateCode: templateCodePayload,
        };

        if (useSaveEndpoint) {
          await axiosInstance.post(
            `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/save`,
            payload,
          );
        } else {
          await axiosInstance[isUpdate ? "put" : "post"](
            `${process.env.REACT_APP_API_BASE_URL}/scheduled-events`,
            payload,
          );
        }
      } else {
        const payload = {
          scheduledCronJobId: idOverride,
          cronJobTypeId: item.cronJobTypeId,
          taskName,
          notificationTypeIds: selectedNotificationTypeIds,
          toEmailSourceFrom: normalizeToEmailSourceFrom(toEmailSourceFrom),
          fromEmailSource: normalizeFromEmailSource(fromEmailSource),
          fromEmail: resolvedFromEmail,
          cronJobExpression: buildCronExpression(cronHour, cronMinute),
          triggerDate,
          days: Number(days) || 0,
          status: STATUS_SENT_TO_BACKEND,
          employeeId: Number(authenticatedEmployeeId) || 0,
          companyId: Number(authenticatedCompanyId) || 0,
          cronJobTemplate: templateFields,
          notifyToSource: notifyToSourcePayload,
          notifyToEmails: notifyToEmailsPayload,
          templateCode: templateCodePayload,
          templateStatus: "",
        };

        if (useSaveEndpoint) {
          await axiosInstance.post(
            `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/save`,
            payload,
          );
        } else {
          await axiosInstance[isUpdate ? "put" : "post"](
            `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs`,
            payload,
          );
        }
      }

      toast.success(
        isEvent
          ? "Scheduled event saved successfully."
          : "Scheduled automation saved successfully.",
      );

      onSaved();
    } catch (err) {
      console.error("Error saving launched template:", err);

      toast.error("Failed to save. Please check the fields and try again.");

      setError("Failed to save. Please check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  // A template from another application is copied into this one; an owned
  // template retains its id and is updated in place.
  const validateTemplateFields = () => {
    const required = [
      ...(showEmailFields ? EMAIL_FIELDS : []),
      ...(showInAppFields ? IN_APP_FIELDS : []),
    ];
    const nextErrors = {};
    required.forEach((field) => {
      if (isFieldEmpty(field, templateFields[field])) {
        nextErrors[field] = true;
      }
    });
    setFieldErrors(nextErrors);
    return nextErrors;
  };

  const handleDirectSave = () => {
    const nextErrors = validateTemplateFields();
    const missing = Object.keys(nextErrors);
    if (missing.length) {
      toast.error(
        "Please fill in the required details: " +
          [
            missing.some((f) => EMAIL_FIELDS.includes(f)) &&
              "Email subject and body",
            missing.some((f) => IN_APP_FIELDS.includes(f)) &&
              "Notification heading and message",
          ]
            .filter(Boolean)
            .join(" and ") +
          ".",
      );
      // Focus the first invalid field (inputs and ReactQuill both expose focus()).
      const first = [...EMAIL_FIELDS, ...IN_APP_FIELDS].find(
        (f) => nextErrors[f],
      );
      const el = templateFieldRefs.current[first];
      el?.focus?.();
      return;
    }
    doSave(item.alreadyUsed ? item[idField] || 0 : null);
  };

  // Download stays disabled until the user actually changes something.
  // The baseline keeps following the form until the first user interaction,
  // so values that load asynchronously (email configs, notification types)
  // are not mistaken for edits; after that it is frozen and we compare.
  const formSnapshot = JSON.stringify({
    taskName,
    toEmailSourceFrom,
    fromEmailSource,
    cronHour,
    cronMinute,
    triggerDate,
    days,
    notificationTypes: [...selectedNotificationTypeIds].sort(),
    templateFields,
    selectedFromEmailConfig,
    targets: selectedTargetIds.map(String).sort(),
  });
  const baselineSnapshotRef = useRef(formSnapshot);
  const userEditedRef = useRef(false);
  if (!userEditedRef.current) baselineSnapshotRef.current = formSnapshot;
  const markEdited = () => {
    userEditedRef.current = true;
  };
  const hasChanges = formSnapshot !== baselineSnapshotRef.current;

  const canSubmit =
    toEmailSourceFrom && fromEmailSource && (isEvent || triggerDate);

  return (
    <div className="launched-template-review-overlay">
      <div className="launched-template-review-modal">
        <div className="review-header">
          <h2>Customize {isEvent ? "notification" : "automation"}</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div
          className="review-layout"
          onChangeCapture={markEdited}
          onInputCapture={markEdited}
          onKeyDownCapture={markEdited}
          onClickCapture={markEdited}
        >
          <div className="review-body">
            <div className="field-row two-col">
              <div>
                <label>Task name</label>
                <input
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                />
              </div>
            </div>

            <div className="field-row two-col">
              <div>
                <label>Sender type</label>
                <select
                  value={fromEmailSource}
                  onChange={(e) => {
                    setFromEmailSource(e.target.value);
                    setSelectedFromEmailConfig("");
                  }}
                >
                  <option value="">Select...</option>
                  {fromEmailOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Recipient type</label>
                <select
                  value={toEmailSourceFrom}
                  onChange={(e) => {
                    setToEmailSourceFrom(e.target.value);
                    setSelectedTargetIds([]);
                  }}
                >
                  <option value="">Select...</option>
                  {toEmailOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mapping-status" aria-live="polite">
              {isLoadingMappings
                ? "Loading mapped email sources…"
                : mappings
                  ? "Email sources mapped to this event are shown above."
                  : "Using the standard email source list."}
            </div>

            {(fromEmailSource === "COMPANY" ||
              fromEmailSource === "SPECIFIC" ||
              showTargetPicker) && (
              <div className="field-row two-col">
                {(fromEmailSource === "COMPANY" ||
                  fromEmailSource === "SPECIFIC") && (
                  <div>
                    <label>
                      {fromEmailSource === "COMPANY"
                        ? "Company email"
                        : "Specific email configuration"}
                    </label>
                    <select
                      value={selectedFromEmailConfig}
                      onChange={(e) =>
                        setSelectedFromEmailConfig(e.target.value)
                      }
                    >
                      <option value="">Select an email…</option>
                      {(fromEmailSource === "COMPANY"
                        ? companyEmailConfigs
                        : specificEmailConfigs
                      ).map((config) => (
                        <option
                          key={config.emailConfigid}
                          value={config.emailConfigid}
                        >
                          {config.fromEmail}
                          {config.fullName ? ` (${config.fullName})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showTargetPicker && (
                  <div>
                    <label>
                      Select {readableLabel(toEmailSourceFrom).toLowerCase()}
                    </label>
                    <Select
                      isMulti
                      classNamePrefix="react-select"
                      className="recipient-multi-select"
                      isLoading={isLoadingAuthorities}
                      noOptionsMessage={() =>
                        isLoadingAuthorities
                          ? "Loading…"
                          : "No options available"
                      }
                      options={targetOptions}
                      value={targetOptions.filter((option) =>
                        selectedTargetIds
                          .map(String)
                          .includes(String(option.value)),
                      )}
                      onChange={(selected) =>
                        setSelectedTargetIds(
                          (selected || []).map((option) => option.value),
                        )
                      }
                      placeholder={`Select ${readableLabel(toEmailSourceFrom).toLowerCase()}…`}
                    />
                  </div>
                )}
              </div>
            )}

            {!isEvent && (
              <>
                <div className="field-row two-col">
                  <div>
                    <label>Time (hour : minute)</label>
                    <div className="time-picker">
                      <select
                        value={cronHour}
                        onChange={(e) => setCronHour(e.target.value)}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>
                            {String(h).padStart(2, "0")}
                          </option>
                        ))}
                      </select>
                      <span>:</span>
                      <select
                        value={cronMinute}
                        onChange={(e) => setCronMinute(e.target.value)}
                      >
                        {Array.from({ length: 60 }, (_, m) => (
                          <option key={m} value={m}>
                            {String(m).padStart(2, "0")}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label>Send timing</label>
                    <div className="trigger-date-row">
                      <div className="trigger-date-options">
                        {TRIGGER_DATE_OPTIONS.map((opt) => (
                          <button
                            type="button"
                            key={opt}
                            className={`trigger-date-button ${
                              triggerDate === opt ? "active" : ""
                            }`}
                            onClick={() => {
                              setTriggerDate(opt);
                              if (opt === "OnDATE") setDays("0");
                            }}
                          >
                            {triggerDateLabel(opt)}
                          </button>
                        ))}
                      </div>
                      {/* OnDATE fires exactly on the date itself, so there's
                          no offset to collect. DateBEFORE/DateAFTER need it. */}
                      {(triggerDate === "DateAFTER" ||
                        triggerDate === "DateBEFORE") && (
                        <div className="trigger-date-days">
                          <input
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    {!triggerDateMatched && item.triggerDate && (
                      <span className="field-hint warning">
                        Previous value "{item.triggerDate}" didn't match an enum
                        - please confirm.
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="field-row">
              <label>Notification types</label>
              <div className="notification-type-checkboxes">
                {notificationTypeOptions.map((n) => (
                  <label key={n.notificationTypeId} className="checkbox-pill">
                    <input
                      type="checkbox"
                      checked={selectedNotificationTypeIds.includes(
                        n.notificationTypeId,
                      )}
                      onChange={() =>
                        toggleNotificationType(n.notificationTypeId)
                      }
                    />
                    {n.label || readableLabel(n.notificationType)}
                  </label>
                ))}
                {notificationTypeOptions.length === 0 && (
                  <span className="empty-hint">
                    No notification types on this template.
                  </span>
                )}
              </div>
            </div>

            <fieldset className="template-fieldset">
              <legend>Template</legend>

              {!showEmailFields && !showInAppFields && (
                <p className="empty-hint">
                  Select a notification type above (Email / In-app) to edit its
                  template fields.
                </p>
              )}

              {showEmailFields && (
                <>
                  <div className="field-row">
                    <label>
                      Subject <span className="required-mark">*</span>
                    </label>
                    <input
                      className={fieldErrors.subject ? "input-error" : ""}
                      aria-invalid={!!fieldErrors.subject}
                      ref={(element) => {
                        templateFieldRefs.current.subject = element;
                      }}
                      value={templateFields.subject}
                      onFocus={() => setActiveTemplateField("subject")}
                      onChange={(e) =>
                        updateTemplateField("subject", e.target.value)
                      }
                    />
                    {fieldErrors.subject && (
                      <span className="field-error-text">
                        Subject is required.
                      </span>
                    )}
                  </div>
                  <div className="field-row">
                    <label>
                      Body <span className="required-mark">*</span>
                    </label>
                    <ReactQuill
                      ref={(element) => {
                        templateFieldRefs.current.body = element;
                      }}
                      className={`email-body-editor ${
                        fieldErrors.body ? "input-error" : ""
                      }`}
                      theme="snow"
                      value={templateFields.body}
                      onFocus={() => setActiveTemplateField("body")}
                      onChange={(value) => updateTemplateField("body", value)}
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline", "strike"],
                          [{ header: [1, 2, 3, false] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link", "clean"],
                        ],
                      }}
                    />
                    {fieldErrors.body && (
                      <span className="field-error-text">
                        Email body is required.
                      </span>
                    )}
                  </div>
                </>
              )}

              {showInAppFields && (
                <div className="field-row two-col">
                  <div>
                    <label>
                      Notification heading{" "}
                      <span className="required-mark">*</span>
                    </label>
                    <input
                      className={
                        fieldErrors.notificationHeading ? "input-error" : ""
                      }
                      aria-invalid={!!fieldErrors.notificationHeading}
                      ref={(element) => {
                        templateFieldRefs.current.notificationHeading = element;
                      }}
                      value={templateFields.notificationHeading}
                      onFocus={() =>
                        setActiveTemplateField("notificationHeading")
                      }
                      onChange={(e) =>
                        updateTemplateField(
                          "notificationHeading",
                          e.target.value,
                        )
                      }
                    />
                    {fieldErrors.notificationHeading && (
                      <span className="field-error-text">
                        Notification heading is required.
                      </span>
                    )}
                  </div>
                  <div>
                    <label>
                      Notification message{" "}
                      <span className="required-mark">*</span>
                    </label>
                    <input
                      className={
                        fieldErrors.notificationMessage ? "input-error" : ""
                      }
                      aria-invalid={!!fieldErrors.notificationMessage}
                      ref={(element) => {
                        templateFieldRefs.current.notificationMessage = element;
                      }}
                      value={templateFields.notificationMessage}
                      onFocus={() =>
                        setActiveTemplateField("notificationMessage")
                      }
                      onChange={(e) =>
                        updateTemplateField(
                          "notificationMessage",
                          e.target.value,
                        )
                      }
                    />
                    {fieldErrors.notificationMessage && (
                      <span className="field-error-text">
                        Notification message is required.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Placeholders can be inserted into whichever field currently
                  has focus (subject/body for EMAIL, heading/message for
                  IN_APP - see insertPlaceholder), so this shows whenever
                  either type is active rather than being EMAIL-only. */}
              {(showEmailFields || showInAppFields) && (
                <div className="placeholder-reference">
                  <span className="placeholder-reference__title">
                    Placeholders
                  </span>
                  <div className="placeholder-reference__list">
                    {mappings?.placeHolders?.map((placeholder) => (
                      <button
                        type="button"
                        key={
                          placeholder.placeHolderId ?? placeholder.placeHolder
                        }
                        className="placeholder-chip"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() =>
                          insertPlaceholder(placeholder.placeHolder)
                        }
                      >
                        {placeholder.placeHolder}
                        {placeholder.description && (
                          <span
                            className="placeholder-chip__tooltip"
                            role="tooltip"
                          >
                            {placeholder.description}
                          </span>
                        )}
                      </button>
                    ))}
                    {!isLoadingMappings && !mappings?.placeHolders?.length && (
                      <span className="empty-hint">
                        No placeholders available.
                      </span>
                    )}
                  </div>
                </div>
              )}
            </fieldset>
          </div>

          <aside className="review-live-preview">
            <div className="review-live-preview__header">
              <div>
                <span>
                  {previewMode === "look"
                    ? "Rendered template"
                    : "Current template preview"}
                </span>
                <p>
                  {previewMode === "look"
                    ? "How this template was rendered and sent"
                    : "Updates as you edit the form"}
                </p>
              </div>
              {(previewMode === "look"
                ? templatePreview?.htmlBody
                : templateFields.htmlBody) && (
                <span className="review-live-preview__badge">HTML</span>
              )}
            </div>

            {templatePreview && (
              <div className="review-live-preview__mode-toggle">
                <button
                  type="button"
                  className={previewMode === "live" ? "active" : ""}
                  onClick={() => setPreviewMode("live")}
                >
                  Current changes
                </button>
                <button
                  type="button"
                  className={previewMode === "look" ? "active" : ""}
                  onClick={() => setPreviewMode("look")}
                >
                  Sent version
                </button>
              </div>
            )}

            {previewMode === "look" && templatePreview ? (
              <>
                {templatePreview.subject && (
                  <p className="review-live-preview__subject">
                    {templatePreview.subject}
                  </p>
                )}
                <EmailPreview
                  body={templatePreview.body}
                  htmlBody={templatePreview.htmlBody}
                  label="Rendered template"
                  variant="editor"
                />
                {!templatePreview.body && (
                  <div className="review-live-preview__empty">
                    No rendered preview is available for this template.
                  </div>
                )}
              </>
            ) : (
              <>
                {templateFields.subject && (
                  <p className="review-live-preview__subject">
                    {templateFields.subject}
                  </p>
                )}
                <EmailPreview
                  body={templateFields.body}
                  htmlBody={templateFields.htmlBody}
                  label="Email template"
                  variant="editor"
                />
                {!templateFields.body && (
                  <div className="review-live-preview__empty">
                    Start typing a body to see the email here.
                  </div>
                )}
              </>
            )}
          </aside>
        </div>

        <div className="review-footer">
          <button
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={handleDirectSave}
            disabled={saving || !canSubmit || !hasChanges}
            title={hasChanges ? undefined : "Make a change to enable Download"}
          >
            {saving ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LaunchedTemplateReviewForm;
